import { cn } from '../cn'

/**
 * A phone field.
 *
 * §0.2 says a shared component is built *when the first module needing it lands, from that page's
 * markup, rather than invented now*. Two modules the ledger names — `manager-hr` and
 * `manager-customers` — have landed, and phone entry is inlined across the port. Measured: **seven**
 * screens take a phone number and **three** of them set `inputMode="tel"`. The other four hand a phone
 * user the full alphabetic keyboard to type a number into, on a port whose §14.8 rule is phone-first.
 *
 * So this is `NewOrderPage`'s markup — the one that was already right — extracted rather than designed.
 * Nothing about it is new except that it is now in one place:
 *
 * - `inputMode="tel"` so a phone offers the keypad;
 * - `autoComplete="tel"` so a browser can fill it;
 * - `type="text"`, deliberately **not** `type="tel"`: the two behave the same on mobile keyboards, and
 *   `tel` adds a spellcheck-free free-text field with no validation, which reads as validation to the
 *   next person and provides none.
 *
 * **It does not format as you type.** `formatPhone` exists in `@agos/shared` for *display*, and running
 * it over an input on every keystroke moves the caret and fights anybody pasting an international
 * number — Slovak numbers arrive as `+421…`, `0…` and `00421…`, and the server normalises. The field
 * keeps what was typed and `toE164` is applied where it is sent.
 */
export function PhoneInput({
  value,
  onChange,
  label,
  placeholder,
  className,
  disabled,
  testId,
}: {
  value: string
  onChange: (value: string) => void
  /** Used as the accessible name. Every existing site already passes one. */
  label: string
  placeholder?: string
  className?: string
  disabled?: boolean
  testId?: string
}) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={label}
      placeholder={placeholder}
      disabled={disabled}
      data-phone-input={testId ?? ''}
      type="text"
      inputMode="tel"
      autoComplete="tel"
      className={cn(
        'w-full rounded-xl bg-card border border-border px-3 py-2 text-sm outline-none',
        'focus:border-primary disabled:opacity-50',
        className,
      )}
    />
  )
}
